import React from "react";
import { motion } from "framer-motion";
import { Fingerprint, Briefcase, ShieldCheck, MessageCircle } from "lucide-react";
import { Reveal, Overline, MaskedHeading } from "@/components/ui-bits";
import { stagger, fadeUp, viewport } from "@/lib/motion";
import { WHATSAPP_URL, WHATSAPP_DISPLAY, BOOKING_URL } from "@/lib/api";

const ITEMS = [
  {
    icon: Fingerprint,
    title: "Private Investigations",
    text: "Background checks, relationship due diligence, missing-person enquiries, and fact-finding — handled with complete discretion.",
  },
  {
    icon: Briefcase,
    title: "Business Intelligence",
    text: "Vetting new suppliers, business partners, or vendors before you sign. Protect your business from local fraud risk.",
  },
  {
    icon: ShieldCheck,
    title: "Security & Risk",
    text: "Security assessments, business security reviews, and event security planning and consultancy.",
  },
];

export default function LocalServices() {
  return (
    <section
      id="local-services"
      className="relative bg-paper py-24 sm:py-32"
      data-testid="local-services-section"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="max-w-3xl">
          <Reveal>
            <Overline index={10}>Local Ghana Services</Overline>
          </Reveal>
          <MaskedHeading
            tag="h2"
            className="mt-6 font-display font-bold text-navy leading-[1.05] tracking-tight text-3xl sm:text-4xl lg:text-5xl"
            lines={["Already in Ghana?", "We Can Help Locally, Too."]}
          />
          <Reveal delay={0.1}>
            <p className="mt-6 text-ink/70 leading-relaxed">
              The same licensed investigator trusted by international clients
              also serves individuals and businesses right here in Ghana.
              Discreet, professional, and confidential —{" "}
              <span className="text-navy font-semibold italic">
                before you trust, verify.
              </span>
            </p>
          </Reveal>
        </div>

        <motion.div
          variants={stagger(0.12)}
          initial="hidden"
          whileInView="visible"
          viewport={viewport}
          className="mt-14 grid md:grid-cols-3 gap-px bg-navy/10 border border-navy/10"
        >
          {ITEMS.map((it, i) => {
            const Icon = it.icon;
            return (
              <motion.div
                key={it.title}
                variants={fadeUp}
                className="group bg-paper hover:bg-white transition-colors duration-500 p-8 sm:p-10"
                data-testid={`local-card-${i + 1}`}
              >
                <div className="w-12 h-12 flex items-center justify-center border border-gold/40 text-gold group-hover:bg-gold group-hover:text-navy-deep transition-colors duration-500">
                  <Icon size={22} />
                </div>
                <h3 className="mt-8 font-display text-2xl font-semibold text-navy">
                  {it.title}
                </h3>
                <p className="mt-3 text-ink/65 leading-relaxed text-[15px]">
                  {it.text}
                </p>
              </motion.div>
            );
          })}
        </motion.div>

        {/* WhatsApp-first CTA block */}
        <Reveal delay={0.1} className="mt-14">
          <div className="relative bg-navy border border-gold/25 p-8 sm:p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <div className="text-xs uppercase tracking-[0.25em] text-gold">
                Primary Channel · Local Clients
              </div>
              <div className="mt-3 font-display text-2xl sm:text-3xl text-white leading-tight">
                Chat With Us on WhatsApp
              </div>
              <div className="mt-2 text-white/60 text-sm">
                Fastest way to reach us — {WHATSAPP_DISPLAY}
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="local-whatsapp-cta"
                className="inline-flex items-center justify-center gap-2 bg-[#25D366] text-white hover:bg-[#20b558] transition-colors duration-300 font-semibold px-7 py-4 text-sm uppercase tracking-wider"
              >
                <MessageCircle size={18} />
                Chat With Us on WhatsApp
              </a>
              <a
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="local-book-cta"
                className="inline-flex items-center justify-center gap-2 border border-white/25 text-white/85 hover:border-gold hover:text-gold transition-colors duration-300 font-semibold px-7 py-4 text-xs uppercase tracking-wider"
              >
                Or Book a Consultation
              </a>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
